import { css, FlattenInterpolation, Interpolation, SimpleInterpolation, ThemedStyledProps } from 'styled-components';
import { DefaultTheme } from 'types/styled-components';

const deviceMap = {
    MOBILE_SML: 'mobileSml',
    MOBILE_MED: 'mobileMed',
    MOBILE_LRG: 'mobileLrg',
    TABLET: 'tablet',
    DESKTOP_SML: 'desktopSml',
    DESKTOP_MED: 'desktopMed',
    DESKTOP_LRG: 'desktopLrg',
    MOBILE_MID_HEIGHT: 'mobileMidHeight',
    MOBILE_LRG_HEIGHT: 'mobileLrgHeight',
    IPHONE_X_HEIGHT: 'iphoneXHeight',
    IPHONE_EIGHT_PLUS_HEIGHT: 'iphoneEightPlusHeight',
} as const;

type deviceType = typeof deviceMap[keyof typeof deviceMap];
type breakpoints = { [K in deviceType]: number };

export const {
    MOBILE_SML,
    MOBILE_MED,
    MOBILE_LRG,
    TABLET,
    DESKTOP_SML,
    DESKTOP_MED,
    DESKTOP_LRG,
    MOBILE_MID_HEIGHT,
    MOBILE_LRG_HEIGHT,
    IPHONE_X_HEIGHT,
    IPHONE_EIGHT_PLUS_HEIGHT,
} = deviceMap;

export const breakpoints: breakpoints = {
    [MOBILE_SML]: 320,
    [MOBILE_MED]: 375,
    [MOBILE_LRG]: 425,
    [TABLET]: 768,
    [DESKTOP_SML]: 960,
    [DESKTOP_MED]: 1200,
    [DESKTOP_LRG]: 1440,
    [MOBILE_MID_HEIGHT]: 540,
    [MOBILE_LRG_HEIGHT]: 667,
    [IPHONE_X_HEIGHT]: 635,
    [IPHONE_EIGHT_PLUS_HEIGHT]: 622,
};

export type Breakpoint = deviceType;

const mediaQuery = (type: 'min-width' | 'max-width' | 'width' | 'min-height' | 'max-height', offset = 0) => {
    const baseFontSize = 16;
    const deviceTypes = Object.keys(breakpoints) as deviceType[];
    const initialValue = {} as Record<
        deviceType,
        <T = {}>(
            literals: TemplateStringsArray,
            ...args: Array<SimpleInterpolation | Interpolation<ThemedStyledProps<T, DefaultTheme>>>
        ) => FlattenInterpolation<ThemedStyledProps<T, DefaultTheme>>
    >;

    return deviceTypes.reduce((accumulator, label) => {
        const emSize = (breakpoints[label] + offset) / baseFontSize;
        accumulator[label] = (literals, ...args) => css`
            @media (${type}: ${emSize}em) {
                ${css(literals, ...args)}
            }
        `;
        return accumulator;
    }, initialValue);
};

export const exactBreakpoint = mediaQuery('width');
export const fromBreakpoint = mediaQuery('min-width');
export const tillBreakpoint = mediaQuery('max-width', -1);
export const tillExactBreakpoint = mediaQuery('max-width');
export const fromHeight = mediaQuery('min-height');
export const tillHeight = mediaQuery('max-height', -1);
